import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { API_URL } from '../utils/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Create a stable API instance that always uses current token
  const api = useCallback(() => {
    const currentToken = localStorage.getItem('token');
    return axios.create({
      baseURL: `${API_URL}/api`,
      headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {}
    });
  }, []);

  // Fetch user on mount or when token changes
  useEffect(() => {
    const fetchUser = async () => {
      const storedToken = localStorage.getItem('token');
      if (storedToken) {
        try {
          const res = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` }
          });
          setUser(res.data);
          setToken(storedToken);
        } catch (err) {
          console.error('Failed to fetch user:', err);
          localStorage.removeItem('token');
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    fetchUser();
  }, []);

  const login = async (email, password) => {
    const res = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const newToken = res.data.token;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (name, email, password, phone, location, promo_code, suburb, state, country) => {
    const res = await axios.post(`${API_URL}/api/auth/register`, { 
      name, 
      email, 
      password, 
      phone, 
      location, 
      promo_code,
      suburb,
      state,
      country
    });
    const newToken = res.data.token;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(res.data.user);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    const currentToken = localStorage.getItem('token');
    if (currentToken) {
      try {
        const res = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        setUser(res.data);
      } catch (err) {
        console.error('Failed to refresh user', err);
      }
    }
  };

  const isSubscribed = useCallback(() => {
    if (!user) return false;
    const status = user.subscription_status;
    if (status === 'active') return true;
    if (status === 'trial' && user.trial_ends_at) {
      return new Date(user.trial_ends_at) > new Date();
    }
    return false;
  }, [user]);

  const canSell = useCallback(() => {
    if (!user) return false;
    if (!user.is_seller) return false;
    return isSubscribed();
  }, [user, isSubscribed]);

  const needsPaymentSetup = useCallback(() => {
    if (!user) return false;
    // Check if user is in pending_payment status or has the flag set
    return user.subscription_status === 'pending_payment' || user.payment_setup_required === true;
  }, [user]);

  const getTrialDaysLeft = useCallback(() => {
    if (!user || user.subscription_status !== 'trial' || !user.trial_ends_at) return 0;
    const diff = new Date(user.trial_ends_at) - new Date();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [user]);

  const value = useMemo(() => ({
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    isSubscribed,
    canSell,
    needsPaymentSetup,
    getTrialDaysLeft,
    api
  }), [user, token, loading, isSubscribed, canSell, needsPaymentSetup, getTrialDaysLeft, api]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
